import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSpark } from "@/lib/store";
import { patchProfile } from "@/lib/api";
import { SparkSun, GoldThreadLine } from "@/components/SparkSun";

// Shown once, after a pilot's first sign-in. Reachable again from the footer as "About privacy".
export default function PilotWelcome() {
  const { userId, profile, setProfile } = useSpark();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const review = params.get("review") === "1";

  const begin = async () => {
    if (!profile?.pilot_welcome_seen) setProfile(await patchProfile(userId, "pilot_welcome_seen", true));
    nav(profile?.onboarded && !profile?.c1_active ? "/dashboard" : "/conversation/why-am-i-here");
  };

  return (
    <div data-testid="pilot-welcome" className="max-w-2xl mx-auto py-6">
      <SparkSun size={64} color="#C3A56B" strokeWidth={1} />
      <p className="mt-8 spark-eyebrow-copper spark-eyebrow-with-dot">{review ? "About privacy" : "A quiet word first"}</p>
      <h1 className="mt-3 text-4xl sm:text-5xl leading-tight text-[#17171C] font-medium tracking-tight">Welcome to Spark</h1>
      <div className="mt-8 space-y-6 text-lg leading-relaxed text-[#56565D]">
        <p>
          Spark is a private space to work through your career at your own pace.
          <span className="spark-voice text-[#17171C]"> What you write here is yours.</span> Your reflections,
          activities and Career Compass aren't automatically shared with me or anyone else.
        </p>
        <p>If you're using Spark alongside coaching, you can choose what you'd like to share with your coach at any time.</p>
        <p>
          Spark uses AI to help notice patterns and offer reflections based on what you tell it. It won't always get
          things right, so you'll regularly be able to confirm, correct or reject what it notices.
          <span className="spark-voice text-[#17171C]"> Your corrections help Spark better understand what you mean.</span>
        </p>
      </div>
      <GoldThreadLine className="w-40 h-2 mt-10" />
      <div className="mt-8 flex flex-wrap gap-3">
        {review ? (
          <button data-testid="pilot-welcome-back-btn" onClick={() => nav(-1)} className="spark-btn-secondary">Back</button>
        ) : (
          <button data-testid="pilot-welcome-start-btn" onClick={begin} className="spark-btn-primary text-base px-7 py-4">
            Start my Spark journey <ArrowRight size={18} className="ml-2" />
          </button>
        )}
      </div>
    </div>
  );
}
